import type { StateCreator } from 'zustand';
import type { StandardLoanState } from '../standardLoanState';
import {
    defaultSetupLoanContext,
    emptySetupForm,
    SetupForm,
    SetupLoanContext,
} from '../../components/Steps/components/SetupAndBorrower/Setup/constants/setupForm';
import { calculateMaturityDate } from '../../components/Steps/components/SetupAndBorrower/Setup/functions/calculateMaturityDate';

/** The Setup step: its fields, and what it needs to know about the loan being added/edited. */
export interface SetupSlice {
    /** The Setup step's fields. */
    setup: SetupForm;
    /**
     * Merges changes into the Setup form; recalculates the Maturity Date when Note Date or Initial Term changes, and
     * tells the Loan slice when the Loan Type changes (applyLoanTypeDefaults).
     */
    updateSetup: (changes: Partial<SetupForm>) => void;
    /**
     * True while the Maturity Date is the one calculated from Note Date + Initial Term (suppresses the terms warning);
     * false once it's typed or cleared by hand. Screen state only, so it's kept out of SetupForm.
     */
    maturityDateAutoCalculated: boolean;
    /**
     * The Term dropdown's choice: a preset key from getLoanTermOptions, CUSTOM_TERM_KEY, or '' for none. Screen state
     * only (the loan saves the term months), so it's kept out of SetupForm.
     */
    selectedTermKey: string;
    /** Picks a Term option and applies its term months (a preset's months, Custom's cleared ones) through updateSetup. */
    selectTerm: (termKey: string, termMonths?: Partial<SetupForm>) => void;

    /** Set by whoever opens the modal. */
    setupLoanContext: SetupLoanContext;
    setSetupLoanContext: (context: Partial<SetupLoanContext>) => void;
}

/** The Setup slice's starting values; reset() puts these back. */
export const initialSetupState = {
    setup: emptySetupForm,
    maturityDateAutoCalculated: false,
    selectedTermKey: '',
    setupLoanContext: defaultSetupLoanContext,
};

export const createSetupSlice: StateCreator<StandardLoanState, [], [], SetupSlice> = (set, get) => ({
    ...initialSetupState,

    // set(state => ...) merges into the latest state, so back-to-back field changes never overwrite each other
    updateSetup: (changes) => {
        set((state) => {
            const setup = { ...state.setup, ...changes };
            // A Maturity Date passed in was typed or cleared by hand, so the terms warning applies again
            let maturityDateAutoCalculated = 'maturityDate' in changes ? false : state.maturityDateAutoCalculated;
            // Same trigger as EditStdLoanModal: the Maturity Date follows Note Date + Initial Term
            if ('noteDate' in changes || 'initialTermMonths' in changes) {
                const maturityDate = calculateMaturityDate(setup, state.setupLoanContext);
                if (maturityDate) {
                    setup.maturityDate = maturityDate;
                    maturityDateAutoCalculated = true;
                }
            }
            return { setup, maturityDateAutoCalculated };
        });
        // The Loan slice decides what a new Loan Type means for its own fields
        if ('loanTypeId' in changes) {
            get().applyLoanTypeDefaults(get().setup.loanTypeId);
        }
    },

    selectTerm: (termKey, termMonths) => {
        set({ selectedTermKey: termKey });
        // Setting initialTermMonths also recalculates the Maturity Date
        if (termMonths) {
            get().updateSetup(termMonths);
        }
    },

    setSetupLoanContext: (context) => set((state) => ({ setupLoanContext: { ...state.setupLoanContext, ...context } })),
});
